import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Check, AlertCircle, RefreshCw, Loader2, ExternalLink, Clock, Inbox, X } from 'lucide-react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';

/**
 * Admin — Logs des envois d'emails.
 *
 * Trace TOUS les envois transactionnels (newsletter welcome, contact accusé,
 * contact admin notif, contact reply admin, password reset, teacher app, etc.)
 * via la collection MongoDB `email_logs`.
 *
 * Permet à l'admin de débugger un "j'ai pas reçu mon email" sans ouvrir Resend.
 */
export default function EmailLogs() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, ok: 0, error: 0, skipped: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminListEmailLogs(filter === 'all' ? null : filter, 200);
      setItems(res?.items || []);
      setStats({
        total: res?.total || 0,
        ok: res?.ok || 0,
        error: res?.error || 0,
        skipped: res?.skipped || 0,
      });
    } catch (err) {
      console.error('Email logs load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-email-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Logs des emails</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Trace de tous les envois transactionnels (newsletter, contact, mots de passe, candidatures…). Pour le statut final de livraison (delivered/bounced/complained), consulte directement <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline inline-flex items-center gap-1">Resend.com <ExternalLink className="w-3 h-3" /></a>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-logs-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Mail} label="Total" value={stats.total} testId="stat-total" />
        <Stat icon={Check} label="Envoyés" value={stats.ok} accent="success" testId="stat-ok" />
        <Stat icon={AlertCircle} label="Erreurs" value={stats.error} accent="primary" testId="stat-error" />
        <Stat icon={Inbox} label="Ignorés" value={stats.skipped} accent="muted" testId="stat-skipped" />
      </div>

      {/* Filtres */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Tous</TabsTrigger>
          <TabsTrigger value="ok" data-testid="tab-ok">Envoyés</TabsTrigger>
          <TabsTrigger value="error" data-testid="tab-error">Erreurs</TabsTrigger>
          <TabsTrigger value="skipped" data-testid="tab-skipped">Ignorés</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Liste */}
      <div className="mc-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Mail className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm font-semibold">Aucun log pour ce filtre</p>
            <p className="text-xs text-muted-foreground mt-1">
              Les envois d'emails (newsletter welcome, contact, mots de passe, candidatures) apparaîtront ici dès qu'ils sont déclenchés.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Date</th>
                  <th className="text-left px-4 py-3 font-bold">Statut</th>
                  <th className="text-left px-4 py-3 font-bold">Destinataire</th>
                  <th className="text-left px-4 py-3 font-bold">Sujet</th>
                  <th className="text-left px-4 py-3 font-bold">Détails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((it, idx) => (
                  <tr key={idx} className="hover:bg-muted/20 transition-colors" data-testid={`email-log-row-${idx}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{formatDate(it.created_at)}</div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={it.status} /></td>
                    <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">{it.to}</td>
                    <td className="px-4 py-3 text-sm truncate max-w-[280px]">{it.subject}</td>
                    <td className="px-4 py-3 text-xs">
                      {it.status === 'ok' && it.resend_id && (
                        <span className="font-mono text-muted-foreground" title={`Resend ID: ${it.resend_id}`}>
                          {it.resend_id.substring(0, 10)}…
                        </span>
                      )}
                      {it.status === 'error' && it.error && (
                        <span className="text-[hsl(var(--primary))]" title={it.error}>
                          {it.error.length > 50 ? it.error.substring(0, 50) + '…' : it.error}
                        </span>
                      )}
                      {it.status === 'skipped' && it.error && (
                        <span className="text-muted-foreground italic">{it.error}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =============== Sub-components ===============
function Stat({ icon: Icon, label, value, accent = 'default', testId }) {
  const cls = {
    default: 'border-border',
    primary: 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5',
    success: 'border-green-500/30 bg-green-500/5',
    muted: 'border-border bg-muted/30',
  }[accent];
  const valueColor = {
    default: 'text-foreground',
    primary: 'text-[hsl(var(--primary))]',
    success: 'text-green-600 dark:text-green-400',
    muted: 'text-muted-foreground',
  }[accent];
  return (
    <div className={`mc-card p-4 border ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className={`text-2xl font-black ${valueColor}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    ok: { label: 'Envoyé', cls: 'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30', icon: Check },
    error: { label: 'Erreur', cls: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30', icon: AlertCircle },
    skipped: { label: 'Ignoré', cls: 'bg-muted text-muted-foreground border border-border', icon: X },
  }[status] || { label: status, cls: 'bg-muted text-muted-foreground', icon: AlertCircle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
